/**
 * 班级详情抽屉（class-management:SC-11/12/13/15~SC-24 前端）。
 *
 * 严格对照原型 prototype/admin/src/pages/classes/ClassDetailDrawer.tsx：
 * Drawer + Tabs（成员标签页）；成员表格（姓名/学号/入班时间/在班状态标签）+
 * 顶部「添加成员」按钮（打开内嵌 Modal）+ 行内「移除」Popconfirm
 * （提示「移除后保留离班记录」）；标题旁「编辑」入口打开编辑对话框。
 * 添加失败展示后端错误信封（同 SKU 提示班级名/容量满/无余额/终态禁止）。
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Descriptions,
  Drawer,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Typography,
  message,
} from "antd";
import { DraggableModal } from "../../components/DraggableModal";
import type { ColumnsType } from "antd/es/table";
import {
  classesApi,
  type ClassListItem,
  type ClassMember,
} from "../../api/classes.api";
import { studentsApi } from "../../api/students.api";
import { ApiError } from "../../api/transport";
import { useHasPermission } from "../../auth/HasPermission";
import { ClassStatusTag, EnrollmentStatusTag } from "./ClassStatusTag";
import { ClassEditModal } from "./ClassEditModal";
import { ScheduleRulesTable } from "./ScheduleRulesTable";
import { errorMessage } from "./ClassListPage";

interface ClassDetailDrawerProps {
  classId: string | null;
  onClose: () => void;
  onChanged?: () => void;
}

/** 学生搜索选项。 */
interface StudentOption {
  value: string;
  label: string;
}

export function ClassDetailDrawer({
  classId,
  onClose,
  onChanged,
}: ClassDetailDrawerProps) {
  const [detail, setDetail] = useState<ClassListItem | null>(null);
  const [members, setMembers] = useState<ClassMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<string>();
  const [studentOptions, setStudentOptions] = useState<StudentOption[]>([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(false);
  // 受控页签：Drawer loading 骨架屏会卸载并重挂 Tabs，
  // 非受控时移除/添加成员后的刷新会把页签重置回「摘要」。
  const [activeTab, setActiveTab] = useState("summary");

  const fetchDetail = useCallback(async () => {
    if (!classId) return;
    setLoading(true);
    try {
      const [d, m] = await Promise.all([
        classesApi.getClass(classId),
        classesApi.listMembers(classId),
      ]);
      setDetail(d);
      setMembers(m);
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  // 抽屉组件复用（不销毁），切换班级视为重新打开，回到默认摘要页签
  useEffect(() => {
    setActiveTab("summary");
  }, [classId]);

  /** 输入联想防抖句柄（300ms，避免每次击键打接口）。 */
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** 重置添加成员弹窗的联想与选择状态。 */
  const resetAddState = () => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    setSelectedStudentId(undefined);
    setSearchKeyword("");
    setStudentOptions([]);
  };

  /** 学生联想（输入触发，走脱敏联想接口，按姓名/学号）。 */
  const handleSearch = (keyword: string) => {
    setSearchKeyword(keyword);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!keyword.trim()) {
      setStudentOptions([]);
      return;
    }
    searchTimer.current = setTimeout(() => {
      void (async () => {
        try {
          const items = await studentsApi.searchStudents(keyword.trim());
          setStudentOptions(
            items.map((s) => ({
              value: s.id,
              label: `${s.name}（${s.studentNo}）`,
            })),
          );
        } catch {
          setStudentOptions([]);
        }
      })();
    }, 300);
  };

  /** 添加成员（四步校验链失败展示后端错误）。 */
  const handleAdd = async () => {
    if (!classId || !selectedStudentId) return;
    setSubmitting(true);
    try {
      const result = await classesApi.enrollMember(classId, selectedStudentId);
      if (result.applied) {
        message.success("成员已添加");
      } else {
        message.info("该学生已是本班成员");
      }
      setAddOpen(false);
      resetAddState();
      await fetchDetail();
      onChanged?.();
    } catch (err) {
      // 后端错误信封：同 SKU 提示班级名 / 容量满 / 无余额 / 终态禁止
      message.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  /** 移除成员（Popconfirm 二次确认，软删除保留离班记录）。 */
  const handleRemove = async (studentId: string) => {
    if (!classId) return;
    try {
      await classesApi.removeMember(classId, studentId);
      message.success("已移除，离班记录保留");
      await fetchDetail();
      onChanged?.();
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  const isTerminal =
    detail?.status === "COMPLETED" || detail?.status === "ARCHIVED";
  // 班级写操作统一 ADM-16 EDIT 闸控（编辑/添加成员/移除成员）
  const canManageClass = useHasPermission("ADM-16", "EDIT");

  const memberColumns: ColumnsType<ClassMember> = [
    { title: "姓名", dataIndex: "studentName" },
    { title: "学号", dataIndex: "studentNo" },
    {
      title: "入班时间",
      dataIndex: "joinedAt",
      render: (v: string) => new Date(v).toLocaleDateString(),
    },
    {
      title: "在班状态",
      dataIndex: "status",
      render: (v: ClassMember["status"]) => <EnrollmentStatusTag status={v} />,
    },
    {
      title: "操作",
      fixed: "right",
      render: (_, record) =>
        canManageClass && !isTerminal && record.status === "ACTIVE" ? (
          <Popconfirm
            title="确认移除该成员？"
            description="移除后保留离班记录"
            okText="移除"
            onConfirm={() => void handleRemove(record.studentId)}
          >
            <a style={{ color: "#d4380d" }}>移除</a>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <Drawer
      title={
        <Space>
          <span>{detail?.name ?? "班级详情"}</span>
          {canManageClass && detail && !isTerminal && (
            <Button type="link" size="small" onClick={() => setEditing(true)}>
              编辑
            </Button>
          )}
        </Space>
      }
      open={!!classId}
      onClose={onClose}
      width={720}
      loading={loading}
    >
      {detail && (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "summary",
              label: "摘要",
              children: (
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label="班级名称">
                    {detail.name}
                  </Descriptions.Item>
                  <Descriptions.Item label="状态">
                    <ClassStatusTag status={detail.status} />
                  </Descriptions.Item>
                  <Descriptions.Item label="校区">
                    {detail.campusName}
                  </Descriptions.Item>
                  <Descriptions.Item label="课程SKU">
                    {detail.skuName}
                  </Descriptions.Item>
                  <Descriptions.Item label="容量">
                    {detail.activeCount}/{detail.capacity}（剩余{" "}
                    {detail.remaining}）
                  </Descriptions.Item>
                  <Descriptions.Item label="教师">
                    {detail.teacherName}
                  </Descriptions.Item>
                  <Descriptions.Item label="默认教室">
                    {detail.defaultClassroomName}
                  </Descriptions.Item>
                  <Descriptions.Item label="起止日期">
                    {detail.startDate} ~ {detail.endDate}
                  </Descriptions.Item>
                </Descriptions>
              ),
            },
            {
              key: "members",
              label: "成员",
              children: (
                <Space direction="vertical" style={{ width: "100%" }}>
                  {canManageClass && (
                    <Button
                      type="primary"
                      disabled={isTerminal}
                      onClick={() => setAddOpen(true)}
                    >
                      添加成员
                    </Button>
                  )}
                  <Table
                    scroll={{ x: "max-content" }}
                    rowKey="studentId"
                    dataSource={members}
                    columns={memberColumns}
                    pagination={false}
                    size="small"
                  />
                </Space>
              ),
            },
            {
              key: "schedule",
              label: "排课",
              children: <ScheduleRulesTable classId={detail.id} />,
            },
          ]}
        />
      )}

      {/* 内嵌添加成员 Modal（对齐原型：非独立文件；单个联想式 Select） */}
      <DraggableModal
        title="添加班级成员"
        open={addOpen}
        onOk={() => void handleAdd()}
        onCancel={() => {
          setAddOpen(false);
          resetAddState();
        }}
        confirmLoading={submitting}
        okButtonProps={{ disabled: !selectedStudentId }}
      >
        <Typography.Paragraph type="secondary">
          输入学生姓名或学号查询；添加时校验同 SKU
          唯一在班、班级容量与有效课时余额。
        </Typography.Paragraph>
        <Select
          showSearch
          placeholder="搜索学生姓名 / 学号"
          style={{ width: "100%" }}
          value={selectedStudentId}
          options={studentOptions}
          onSearch={handleSearch}
          onChange={setSelectedStudentId}
          filterOption={false}
          notFoundContent={searchKeyword ? "无匹配学生" : "输入姓名或学号联想"}
        />
      </DraggableModal>

      {/* 标题旁编辑入口打开编辑对话框 */}
      {editing && detail && (
        <ClassEditModal
          editing={detail}
          onClose={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false);
            await fetchDetail();
            onChanged?.();
          }}
        />
      )}
    </Drawer>
  );
}

export { ApiError };
